import React, { useState, useEffect } from 'react';
import { MapPin, Box, Loader2 } from 'lucide-react';
import type { ProductBinLocation } from '../../types/warehouse';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTenantAdminId } from '../../lib/tenantStorage';
import { getProductBinLocation } from '../../lib/services/binInventoryService';
import { formatBinPath } from '../../utils/binLocationFormat';

interface BinLocationBadgeProps {
  productId: string;
  binLocation?: ProductBinLocation | null;
  showFullAddress?: boolean;
  className?: string;
}

export const BinLocationBadge: React.FC<BinLocationBadgeProps> = ({
  productId,
  binLocation: initialLocation,
  showFullAddress = false,
  className = '',
}) => {
  const { user, buyer } = useAuth();
  const adminId = resolveTenantAdminId(user, buyer);
  const [location, setLocation] = useState<ProductBinLocation | null>(initialLocation || null);
  const [loading, setLoading] = useState(!initialLocation);

  useEffect(() => {
    if (initialLocation !== undefined) {
      setLocation(initialLocation);
      setLoading(false);
      return;
    }
    if (!adminId) {
      setLocation(null);
      setLoading(false);
      return;
    }

    const fetchLocation = async () => {
      setLoading(true);
      try {
        const loc = await getProductBinLocation(productId, adminId);
        setLocation(loc);
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, [productId, initialLocation, adminId]);

  if (loading) {
    return (
      <span className={`inline-flex items-center gap-1 text-xs text-gray-400 ${className}`}>
        <Loader2 className="h-3 w-3 animate-spin" />
      </span>
    );
  }

  if (!location) {
    return (
      <span className={`inline-flex items-center gap-1 text-xs text-gray-400 ${className}`}>
        <Box className="h-3 w-3" />
        <span>No bin</span>
      </span>
    );
  }

  const displayText = showFullAddress ? location.full_address : formatBinPath(location);

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 ${className}`}
      title={location.full_address}
    >
      <MapPin className="h-3 w-3" />
      {displayText}
    </span>
  );
};

interface BinLocationDisplayProps {
  binLocations: Map<string, ProductBinLocation>;
  productId: string;
  showFullAddress?: boolean;
}

export const BinLocationDisplay: React.FC<BinLocationDisplayProps> = ({
  binLocations,
  productId,
  showFullAddress = false,
}) => {
  const location = binLocations.get(productId);

  if (!location) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Box className="h-3 w-3" />
        <span>No bin</span>
      </span>
    );
  }

  const displayText = showFullAddress ? location.full_address : formatBinPath(location);

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
      title={location.full_address}
    >
      <MapPin className="h-3 w-3" />
      {displayText}
    </span>
  );
};
